/**
 * Token-bound monograph section surfaces shared by companion demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import type {
  MonographBadgeRailItem,
  MonographBadgeRailProps,
  MonographPanelCardProps,
  MonographPanelShellProps,
  MonographPanelTokens,
} from "./MonographPanelPrimitiveTypes.js";
import { colorWithAlpha } from "./MonographPanelColor.js";

export interface MonographTaggedPanelShellProps extends Omit<
  MonographPanelShellProps,
  "accentColor" | "badges"
> {
  readonly accent: string;
  readonly tags?: readonly string[];
  readonly badges?: readonly MonographBadgeRailItem[];
}

export interface MonographConceptCardProps {
  readonly heading: React.ReactNode;
  readonly children: React.ReactNode;
  readonly aside?: React.ReactNode;
  readonly accent?: string;
}

export interface MonographLabelValueRowProps {
  readonly label: React.ReactNode;
  readonly children: React.ReactNode;
}

export interface MonographTintedRowProps {
  readonly bodyClassName?: string;
  readonly children?: React.ReactNode;
  readonly className?: string;
  readonly color?: string;
  readonly compact?: boolean;
  readonly heading?: React.ReactNode;
  readonly leading?: React.ReactNode;
  readonly muted?: boolean;
  readonly role?: React.AriaRole;
  readonly style?: React.CSSProperties;
  readonly trailing?: React.ReactNode;
}

export interface MonographSectionLabelProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export interface MonographBadgeGroupProps extends Omit<MonographBadgeRailProps, "items"> {
  readonly children?: React.ReactNode;
  readonly items?: readonly MonographBadgeRailItem[];
}

export interface MonographBulletedListProps {
  readonly items: readonly React.ReactNode[];
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly compact?: boolean;
  readonly style?: React.CSSProperties;
}

export interface MonographStackProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly gap?: number;
  readonly style?: React.CSSProperties;
}

interface SectionSurfaceFactoryInput {
  readonly tokens: MonographPanelTokens;
  readonly Card: (props: MonographPanelCardProps) => React.JSX.Element;
  readonly PanelShell: (props: MonographPanelShellProps) => React.JSX.Element;
  readonly BadgeRail: (props: MonographBadgeRailProps) => React.JSX.Element;
}

export function createMonographPanelSectionSurfaces({
  BadgeRail,
  tokens,
  Card,
  PanelShell,
}: SectionSurfaceFactoryInput): {
  readonly BadgeGroup: (props: MonographBadgeGroupProps) => React.JSX.Element;
  readonly BulletedList: (props: MonographBulletedListProps) => React.JSX.Element;
  readonly ConceptCard: (props: MonographConceptCardProps) => React.JSX.Element;
  readonly LabelValueRow: (props: MonographLabelValueRowProps) => React.JSX.Element;
  readonly SectionLabel: (props: MonographSectionLabelProps) => React.JSX.Element;
  readonly Stack: (props: MonographStackProps) => React.JSX.Element;
  readonly TaggedPanelShell: (props: MonographTaggedPanelShellProps) => React.JSX.Element;
  readonly TintedRow: (props: MonographTintedRowProps) => React.JSX.Element;
} {
  function TaggedPanelShell({
    accent,
    badges,
    tags,
    ...props
  }: MonographTaggedPanelShellProps): React.JSX.Element {
    const resolvedBadges = badges ?? tags?.map((tag) => ({ id: tag, label: tag, color: accent }));
    return (
      <PanelShell
        {...props}
        accentColor={accent}
        {...(resolvedBadges !== undefined ? { badges: resolvedBadges } : {})}
      />
    );
  }

  function SectionLabel({
    children,
    className,
    style,
  }: MonographSectionLabelProps): React.JSX.Element {
    return (
      <div
        className={cn("mb-1.5 text-[11px] font-bold uppercase tracking-normal", className)}
        style={{ color: tokens.muted, ...style }}
      >
        {children}
      </div>
    );
  }

  function BadgeGroup({
    ariaLabel = "Badge group",
    children,
    gap,
    items,
    className,
    style,
  }: MonographBadgeGroupProps): React.JSX.Element {
    if (items !== undefined) {
      return (
        <BadgeRail
          items={items}
          ariaLabel={ariaLabel}
          {...(className === undefined ? {} : { className })}
          {...(gap === undefined ? {} : { gap })}
          {...(style === undefined ? {} : { style })}
        />
      );
    }
    return (
      <div
        role="list"
        aria-label={ariaLabel}
        className={cn("flex flex-wrap gap-1.5", className)}
        style={{ gap, ...style }}
      >
        {children}
      </div>
    );
  }

  function Stack({
    children,
    className,
    gap = 12,
    style,
  }: MonographStackProps): React.JSX.Element {
    return (
      <div
        className={cn("csk-monograph-stack", className)}
        style={{ display: "flex", flexDirection: "column", gap, ...style }}
      >
        {children}
      </div>
    );
  }

  function BulletedList({
    items,
    ariaLabel,
    className,
    compact = false,
    style,
  }: MonographBulletedListProps): React.JSX.Element {
    return (
      <ul
        aria-label={ariaLabel}
        className={cn(
          "m-0 flex list-disc flex-col pl-5 text-[12.5px] leading-[1.6]",
          compact ? "gap-0.5" : "gap-1",
          className,
        )}
        style={{ color: tokens.text, ...style }}
      >
        {items.map((item, index) => (
          <li key={typeof item === "string" ? item : index}>{item}</li>
        ))}
      </ul>
    );
  }

  function ConceptCard({
    heading,
    children,
    aside,
    accent = tokens.accent,
  }: MonographConceptCardProps): React.JSX.Element {
    return (
      <Card>
        <header className="mb-3.5 flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="h-[18px] w-1 rounded-sm"
            style={{ background: accent }}
          />
          <h2 className="m-0 text-base font-bold" style={{ color: tokens.bright }}>
            {heading}
          </h2>
        </header>
        <div
          className={
            aside ? "grid gap-[18px] lg:grid-cols-[minmax(0,1fr)_280px]" : "grid gap-[18px]"
          }
        >
          <div className="min-w-0">{children}</div>
          {aside ? (
            <aside
              className="border-t pt-4 lg:border-l lg:border-t-0 lg:pl-[18px] lg:pt-0"
              style={{ borderColor: tokens.border }}
            >
              {aside}
            </aside>
          ) : null}
        </div>
      </Card>
    );
  }

  function LabelValueRow({ label, children }: MonographLabelValueRowProps): React.JSX.Element {
    return (
      <div className="flex items-baseline gap-2 text-xs">
        <span className="min-w-[8rem]" style={{ color: tokens.muted }}>
          {label}
        </span>
        <span className="font-medium" style={{ color: tokens.bright }}>
          {children}
        </span>
      </div>
    );
  }

  function TintedRow({
    bodyClassName,
    children,
    className,
    color = tokens.accent,
    compact = false,
    heading,
    leading,
    muted = false,
    role,
    style,
    trailing,
  }: MonographTintedRowProps): React.JSX.Element {
    const hasHeader = leading !== undefined || heading !== undefined || trailing !== undefined;
    return (
      <div
        className={cn(
          "rounded-[10px] border border-solid",
          compact ? "px-3 py-2" : "px-3.5 py-2.5",
          className,
        )}
        role={role}
        style={{
          background: colorWithAlpha(color, muted ? "0d" : "14"),
          borderColor: colorWithAlpha(color, muted ? "30" : "44"),
          color: tokens.text,
          opacity: muted ? 0.66 : undefined,
          ...style,
        }}
      >
        {hasHeader ? (
          <div className="flex flex-wrap items-center gap-2">
            {leading !== undefined ? (
              <span className="inline-flex shrink-0 items-center" style={{ color }}>
                {leading}
              </span>
            ) : null}
            {heading !== undefined ? (
              <div className="min-w-0 flex-1 font-mono text-[12px] font-bold" style={{ color }}>
                {heading}
              </div>
            ) : null}
            {trailing !== undefined ? (
              <div className="ml-auto inline-flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                {trailing}
              </div>
            ) : null}
          </div>
        ) : null}
        {children !== undefined ? (
          <div
            className={cn("text-[12.5px] leading-[1.6]", hasHeader ? "mt-1.5" : "", bodyClassName)}
          >
            {children}
          </div>
        ) : null}
      </div>
    );
  }

  return {
    BadgeGroup,
    BulletedList,
    ConceptCard,
    LabelValueRow,
    SectionLabel,
    Stack,
    TaggedPanelShell,
    TintedRow,
  };
}
